import React from "react";
import { motion } from "framer-motion";
import {
  SiGoogle, SiApple, SiMeta, SiOpenai, SiNvidia, SiIntel,
  SiSalesforce, SiShopify, SiSpotify, SiNetflix, SiPaypal,
  SiUber, SiAirbnb, SiTesla, SiFlipkart, SiTcs, SiGoldmansachs
} from "react-icons/si";
import { FaLinkedin } from "react-icons/fa6";

// Authentic, high-precision official brand logo SVGs
const companyLogos = {
  Google: (
    <svg viewBox="0 0 24 24" className="w-full h-full object-contain">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
    </svg>
  ),
  Microsoft: (
    <svg viewBox="0 0 24 24" className="w-full h-full object-contain">
      <rect x="1" y="1" width="10.5" height="10.5" fill="#F25022"/>
      <rect x="12.5" y="1" width="10.5" height="10.5" fill="#7FBA00"/>
      <rect x="1" y="12.5" width="10.5" height="10.5" fill="#00A4EF"/>
      <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900"/>
    </svg>
  ),
  Apple: <SiApple className="w-full h-full text-slate-900 object-contain" />,
  Amazon: (
    <div className="w-full h-full flex items-center justify-center p-0.5">
      <svg viewBox="0 0 100 100" className="w-full h-full max-w-full max-h-full object-contain">
        <path fill="#000000" d="M58 52.5c-3 2.5-7.1 3.9-11.5 3.9-9 0-15.3-5.8-15.3-15 0-9.6 6.8-15.3 17.3-15.3 3.3 0 6.4.6 8.9 1.7v-2.1c0-4.9-3.4-7.7-9.2-7.7-4.6 0-8.8 1.4-12.2 3.9l-3.4-6.7C37.4 12.3 43.4 10.5 50.3 10.5c11.3 0 17.5 6 17.5 16.7v26.8h-8.7v-4.6c-.4.8-.8 2.1-1.1 3.1zM57.3 40c-2.2-1-4.8-1.6-7.7-1.6-5.4 0-9 2.7-9 7.2 0 4.4 3.1 6.9 7.9 6.9 3.4 0 6.5-1.4 8.8-3.7V40z"/>
        <path fill="#FF9900" d="M17 65c17.6 12.8 42.3 13.3 59.4 3.3 1.4-.9 3.3.5 2.4 2.1-13.1 13.8-42.3 16.4-61.6 2.4-1.1-.9-.1-2.4 1.4-1.6z"/>
        <path fill="#FF9900" d="M80.2 66.4c-1.1-1.4-6.5-1.5-9-1.1-.4.1-.4-.4 0-.6 2.7-1.7 8.4-1.3 9.3-.2 1 1.1.2 6.9-2.2 8.9-.4.3-.8.1-.6-.3 1-2.1 2.5-6.7 2.5-6.7z"/>
      </svg>
    </div>
  ),
  Meta: (
    <div className="w-full h-full flex flex-col items-center justify-center text-center leading-none select-none">
      <SiMeta className="w-4 h-4 sm:w-5 sm:h-5 md:w-5.5 md:h-5.5 text-[#0668E1]" />
      <span className="text-[7.5px] sm:text-[8.5px] md:text-[9.5px] font-bold text-slate-800 mt-0.5">Meta</span>
    </div>
  ),
  OpenAI: <SiOpenai className="w-full h-full text-slate-900 object-contain" />,
  NVIDIA: <SiNvidia className="w-full h-full text-[#76B900] object-contain" />,

  // Official IBM 8-Bar Blue Logo
  IBM: (
    <svg viewBox="0 0 120 50" className="w-full h-full object-contain">
      <g fill="#0F62FE">
        {/* I */}
        <rect x="5" y="5" width="22" height="4" />
        <rect x="5" y="10.5" width="22" height="4" />
        <rect x="12" y="16" width="8" height="4" />
        <rect x="12" y="21.5" width="8" height="4" />
        <rect x="12" y="27" width="8" height="4" />
        <rect x="12" y="32.5" width="8" height="4" />
        <rect x="5" y="38" width="22" height="4" />
        <rect x="5" y="43.5" width="22" height="4" />
        
        {/* B */}
        <rect x="33" y="5" width="22" height="4" />
        <rect x="33" y="10.5" width="24" height="4" />
        <rect x="33" y="16" width="8" height="4" />
        <rect x="49" y="16" width="8" height="4" />
        <rect x="33" y="21.5" width="23" height="4" />
        <rect x="33" y="27" width="24" height="4" />
        <rect x="33" y="32.5" width="8" height="4" />
        <rect x="49" y="32.5" width="8" height="4" />
        <rect x="33" y="38" width="24" height="4" />
        <rect x="33" y="43.5" width="22" height="4" />

        {/* M */}
        <rect x="62" y="5" width="8" height="4" />
        <rect x="88" y="5" width="8" height="4" />
        <rect x="62" y="10.5" width="10" height="4" />
        <rect x="86" y="10.5" width="10" height="4" />
        <rect x="62" y="16" width="13" height="4" />
        <rect x="83" y="16" width="13" height="4" />
        <rect x="62" y="21.5" width="8" height="4" />
        <rect x="73" y="21.5" width="12" height="4" />
        <rect x="88" y="21.5" width="8" height="4" />
        <rect x="62" y="27" width="8" height="4" />
        <rect x="74.5" y="27" width="9" height="4" />
        <rect x="88" y="27" width="8" height="4" />
        <rect x="62" y="32.5" width="8" height="4" />
        <rect x="88" y="32.5" width="8" height="4" />
        <rect x="62" y="38" width="8" height="4" />
        <rect x="88" y="38" width="8" height="4" />
        <rect x="62" y="43.5" width="8" height="4" />
        <rect x="88" y="43.5" width="8" height="4" />
      </g>
    </svg>
  ),
  Oracle: (
    <svg viewBox="0 0 120 40" className="w-full h-full object-contain">
      <path fill="#C74634" d="M14.1 3.5C6.3 3.5 0 9.8 0 17.6s6.3 14.1 14.1 14.1h15.5c7.8 0 14.1-6.3 14.1-14.1S37.4 3.5 29.6 3.5H14.1zm15.1 22.3H14.5c-4.5 0-8.2-3.7-8.2-8.2s3.7-8.2 8.2-8.2h14.7c4.5 0 8.2 3.7 8.2 8.2s-3.7 8.2-8.2 8.2zM48 4v27.2h6.1V21.1h7.8l6.8 10.1h7.4l-7.9-11c4.2-1.3 6.9-4.8 6.9-9.5 0-5.7-4.1-6.7-10-6.7H48zm6.1 5.3h7.6c3.4 0 5.4 1 5.4 3.8 0 2.9-2.2 3.8-5.4 3.8h-7.6V9.3zM81.5 4L72.2 31.2h6.4l1.9-6.1h9.7l1.9 6.1h6.4L89.2 4h-7.7zm.8 6.4l3.4 10.7h-6.8l3.4-10.7zM102 4v27.2h18v-5.3h-11.9V4H102z"/>
    </svg>
  ),
  Intel: <SiIntel className="w-full h-full text-[#0068B5] object-contain" />,
  Adobe: (
    <div className="w-full h-full flex items-center justify-center p-1">
      <svg viewBox="0 0 24 24" fill="#FF0000" className="w-full h-full max-w-full max-h-full object-contain">
        <path d="M13.96 2H22v20l-8.04-20zM10.04 2H2v20l8.04-20zM12 9.93L17.27 22h-3.95l-1.32-3.41H9.86L12 9.93z"/>
      </svg>
    </div>
  ),
  Salesforce: <SiSalesforce className="w-full h-full text-[#00A1E0] object-contain" />,
  Shopify: <SiShopify className="w-full h-full text-[#95BF47] object-contain" />,
  Spotify: <SiSpotify className="w-full h-full text-[#1DB954] object-contain" />,
  Netflix: <SiNetflix className="w-full h-full text-[#E50914] object-contain" />,
  PayPal: <SiPaypal className="w-full h-full text-[#003087] object-contain" />,
  Uber: <SiUber className="w-full h-full text-slate-900 object-contain" />,
  Airbnb: <SiAirbnb className="w-full h-full text-[#FF5A5F] object-contain" />,
  Tesla: <SiTesla className="w-full h-full text-slate-900 object-contain" />,
  LinkedIn: <FaLinkedin className="w-full h-full text-[#0A66C2] object-contain" />,
  Flipkart: (
    <div className="w-full h-full bg-[#2874F0] rounded-sm flex items-center justify-center p-1">
      <SiFlipkart className="w-full h-full text-amber-300 object-contain" />
    </div>
  ),

  // Official Infosys Corporate Blue Logo
  Infosys: (
    <svg viewBox="0 0 140 45" className="w-full h-full object-contain">
      <text x="5" y="34" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="34" fill="#007CC3" letterSpacing="-1">
        Infosys
      </text>
    </svg>
  ),

  // Official Wipro Logo with Wordmark + Concentric Dots Rosette
  Wipro: (
    <svg viewBox="0 0 160 50" className="w-full h-full object-contain">
      <text x="5" y="36" fontFamily="Outfit, Plus Jakarta Sans, Arial, sans-serif" fontWeight="800" fontSize="36" fill="#111827" letterSpacing="-1">
        wipro
      </text>
      <g transform="translate(132, 24)">
        {/* Outer ring dots */}
        <circle cx="0" cy="-14" r="2.5" fill="#7C3AED" />
        <circle cx="10" cy="-10" r="2.5" fill="#EC4899" />
        <circle cx="14" cy="0" r="2.5" fill="#F59E0B" />
        <circle cx="10" cy="10" r="2.5" fill="#10B981" />
        <circle cx="0" cy="14" r="2.5" fill="#3B82F6" />
        <circle cx="-10" cy="10" r="2.5" fill="#6366F1" />
        <circle cx="-14" cy="0" r="2.5" fill="#8B5CF6" />
        <circle cx="-10" cy="-10" r="2.5" fill="#D946EF" />
        
        {/* Inner ring dots */}
        <circle cx="0" cy="-7" r="2" fill="#EC4899" />
        <circle cx="5" cy="-5" r="2" fill="#F59E0B" />
        <circle cx="7" cy="0" r="2" fill="#10B981" />
        <circle cx="5" cy="5" r="2" fill="#3B82F6" />
        <circle cx="0" cy="7" r="2" fill="#8B5CF6" />
        <circle cx="-5" cy="5" r="2" fill="#D946EF" />
        <circle cx="-7" cy="0" r="2" fill="#7C3AED" />
        <circle cx="-5" cy="-5" r="2" fill="#6366F1" />

        {/* Center dot */}
        <circle cx="0" cy="0" r="2" fill="#111827" />
      </g>
    </svg>
  ),

  TCS: (
    <div className="w-full h-full flex flex-col items-center justify-center text-center leading-none select-none">
      <span className="font-extrabold text-xs sm:text-sm md:text-base tracking-tighter text-slate-900">tcs</span>
      <span className="text-[4.5px] sm:text-[5.5px] md:text-[6px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">TATA CONSULTANCY SERVICES</span>
    </div>
  ),

  // Official Accenture Logo with Wordmark + Purple Chevron Chevron over 't'
  Accenture: (
    <svg viewBox="0 0 160 50" className="w-full h-full object-contain">
      {/* Purple Chevron mark above the 't' */}
      <path fill="#A100FF" d="M84 4 L98 12 L84 20 L87 23 L104 12 L87 1 Z" />
      {/* accenture wordmark */}
      <text x="5" y="42" fontFamily="Arial, Helvetica, sans-serif" fontWeight="800" fontSize="30" fill="#111827" letterSpacing="-0.5">
        accenture
      </text>
    </svg>
  ),

  JPMorgan: (
    <div className="w-full h-full flex items-center justify-center font-extrabold text-[9.5px] sm:text-[11px] md:text-[12.5px] tracking-tighter text-[#0054A6] select-none">
      J.P.Morgan
    </div>
  ),
  GoldmanSachs: (
    <div className="w-full h-full bg-[#7399C6] text-white p-1 rounded-sm flex flex-col items-center justify-center text-center font-bold text-[7.5px] sm:text-[8.5px] md:text-[9.5px] leading-tight select-none">
      <span>Goldman</span>
      <span>Sachs</span>
    </div>
  ),
  Stripe: (
    <div className="w-full h-full bg-[#635BFF] rounded-lg md:rounded-xl flex items-center justify-center text-white font-extrabold text-xs sm:text-sm md:text-base select-none shadow-xs">
      S
    </div>
  )
};

// Size proportions matching the reference image precisely
const sizeClasses = {
  huge: {
    circle: "w-18 h-18 sm:w-22 sm:h-22 md:w-26 md:h-26 lg:w-28 lg:h-28",
    icon: "w-9 h-9 sm:w-11 sm:h-11 md:w-13 md:h-13 lg:w-14 lg:h-14"
  },
  large: {
    circle: "w-15 h-15 sm:w-18 sm:h-18 md:w-20 md:h-20 lg:w-22 lg:h-22",
    icon: "w-7.5 h-7.5 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-11 lg:h-11"
  },
  medium: {
    circle: "w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18",
    icon: "w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9"
  },
  small: {
    circle: "w-10 h-10 sm:w-11 sm:h-11 md:w-13 md:h-13 lg:w-14 lg:h-14",
    icon: "w-5 h-5 sm:w-5.5 sm:h-5.5 md:w-6.5 md:h-6.5 lg:w-7 lg:h-7"
  }
};

const floatingLogos = [
  // LEFT WING CLUSTER
  {
    name: "Google",
    logo: companyLogos.Google,
    size: "huge",
    pos: "top-[10%] left-[5%] lg:left-[7%]",
    zIndex: "z-20",
    visibility: "flex",
    float: { y: [-6, 6, -6], x: [-4, 4, -4], rotate: [-2, 2, -2], duration: 5.4, delay: 0 }
  },
  {
    name: "Microsoft",
    logo: companyLogos.Microsoft,
    size: "large",
    pos: "top-[22%] left-[14%] lg:left-[16%]",
    zIndex: "z-10",
    visibility: "hidden sm:flex",
    float: { y: [6, -6, 6], x: [3, -3, 3], rotate: [2, -2, 2], duration: 6.2, delay: 0.5 }
  },
  {
    name: "Uber",
    logo: companyLogos.Uber,
    size: "large",
    pos: "top-[32%] left-[3%] lg:left-[4.5%]",
    zIndex: "z-15",
    visibility: "flex",
    float: { y: [7, -7, 7], x: [4, -4, 4], rotate: [2, -2, 2], duration: 5.9, delay: 0.3 }
  },
  {
    name: "IBM",
    logo: companyLogos.IBM,
    size: "large",
    pos: "top-[42%] left-[11%] lg:left-[13%]",
    zIndex: "z-15",
    visibility: "hidden md:flex",
    float: { y: [-7, 7, -7], x: [-4, 4, -4], rotate: [-2, 2, -2], duration: 6.7, delay: 1.3 }
  },
  {
    name: "Adobe",
    logo: companyLogos.Adobe,
    size: "medium",
    pos: "top-[50%] left-[3%] lg:left-[4.5%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [6, -6, 6], x: [-3, 3, -3], rotate: [2, -2, 2], duration: 5.1, delay: 1.7 }
  },
  {
    name: "Oracle",
    logo: companyLogos.Oracle,
    size: "medium",
    pos: "top-[60%] left-[12%] lg:left-[14%]",
    zIndex: "z-15",
    visibility: "hidden md:flex",
    float: { y: [-5, 5, -5], x: [3, -3, 3], rotate: [-2, 2, -2], duration: 4.9, delay: 2.0 }
  },
  {
    name: "Airbnb",
    logo: companyLogos.Airbnb,
    size: "huge",
    pos: "top-[68%] left-[4%] lg:left-[6%]",
    zIndex: "z-20",
    visibility: "flex",
    float: { y: [-6, 8, -6], x: [4, -4, 4], rotate: [-2, 2, -2], duration: 5.6, delay: 0.7 }
  },
  {
    name: "Amazon",
    logo: companyLogos.Amazon,
    size: "small",
    pos: "top-[78%] left-[12%] lg:left-[14%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [6, -6, 6], x: [-4, 4, -4], rotate: [2, -2, 2], duration: 6.5, delay: 1.4 }
  },
  {
    name: "Intel",
    logo: companyLogos.Intel,
    size: "small",
    pos: "bottom-[8%] left-[6%] lg:left-[8%]",
    zIndex: "z-15",
    visibility: "hidden md:flex",
    float: { y: [-5, 5, -5], x: [-3, 3, -3], rotate: [-2, 2, -2], duration: 4.8, delay: 1.1 }
  },

  // TOP ARC
  {
    name: "Stripe",
    logo: companyLogos.Stripe,
    size: "medium",
    pos: "top-[10%] left-[24%] lg:left-[26%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [-5, 5, -5], x: [3, -3, 3], rotate: [-2, 2, -2], duration: 5.0, delay: 0.2 }
  },
  {
    name: "Flipkart",
    logo: companyLogos.Flipkart,
    size: "small",
    pos: "top-[8%] left-[50%] -translate-x-1/2",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [-6, 6, -6], x: [3, -3, 3], rotate: [-2, 2, -2], duration: 6.3, delay: 0.1 }
  },
  {
    name: "PayPal",
    logo: companyLogos.PayPal,
    size: "medium",
    pos: "top-[10%] right-[23%] lg:right-[25%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [6, -6, 6], x: [-3, 3, -3], rotate: [2, -2, 2], duration: 5.8, delay: 0.4 }
  },

  // RIGHT WING CLUSTER
  {
    name: "Apple",
    logo: companyLogos.Apple,
    size: "huge",
    pos: "top-[10%] right-[5%] lg:right-[7%]",
    zIndex: "z-20",
    visibility: "flex",
    float: { y: [-6, 6, -6], x: [4, -4, 4], rotate: [-2, 2, -2], duration: 6.4, delay: 0.8 }
  },
  {
    name: "Meta",
    logo: companyLogos.Meta,
    size: "medium",
    pos: "top-[22%] right-[14%] lg:right-[16%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [5, -5, 5], x: [-3, 3, -3], rotate: [2, -2, 2], duration: 5.2, delay: 1.2 }
  },
  {
    name: "Infosys",
    logo: companyLogos.Infosys,
    size: "medium",
    pos: "top-[31%] right-[3%] lg:right-[4.5%]",
    zIndex: "z-15",
    visibility: "hidden md:flex",
    float: { y: [-7, 6, -7], x: [-4, 4, -4], rotate: [-2, 2, -2], duration: 6.0, delay: 0.4 }
  },
  {
    name: "Wipro",
    logo: companyLogos.Wipro,
    size: "huge",
    pos: "top-[40%] right-[7%] lg:right-[9%]",
    zIndex: "z-20",
    visibility: "flex",
    float: { y: [6, -7, 6], x: [4, -4, 4], rotate: [2, -2, 2], duration: 5.5, delay: 1.0 }
  },
  {
    name: "Tesla",
    logo: companyLogos.Tesla,
    size: "medium",
    pos: "top-[50%] right-[3%] lg:right-[4.5%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [-5, 6, -5], x: [3, -3, 3], rotate: [-2, 2, -2], duration: 4.7, delay: 1.8 }
  },
  {
    name: "Salesforce",
    logo: companyLogos.Salesforce,
    size: "small",
    pos: "top-[60%] right-[12%] lg:right-[14%]",
    zIndex: "z-15",
    visibility: "hidden md:flex",
    float: { y: [5, -5, 5], x: [-3, 3, -3], rotate: [2, -2, 2], duration: 5.3, delay: 2.1 }
  },
  {
    name: "LinkedIn",
    logo: companyLogos.LinkedIn,
    size: "huge",
    pos: "top-[68%] right-[4%] lg:right-[6%]",
    zIndex: "z-20",
    visibility: "flex",
    float: { y: [7, -6, 7], x: [-4, 4, -4], rotate: [2, -2, 2], duration: 6.8, delay: 0.9 }
  },
  {
    name: "Netflix",
    logo: companyLogos.Netflix,
    size: "small",
    pos: "top-[78%] right-[11%] lg:right-[13%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [-6, 6, -6], x: [3, -3, 3], rotate: [-2, 2, -2], duration: 6.0, delay: 1.5 }
  },
  {
    name: "NVIDIA",
    logo: companyLogos.NVIDIA,
    size: "medium",
    pos: "bottom-[8%] right-[6%] lg:right-[8%]",
    zIndex: "z-15",
    visibility: "hidden md:flex",
    float: { y: [5, -5, 5], x: [-3, 3, -3], rotate: [2, -2, 2], duration: 4.6, delay: 1.6 }
  },

  // BOTTOM HORIZONTAL ARC
  {
    name: "TCS",
    logo: companyLogos.TCS,
    size: "large",
    pos: "bottom-[8%] left-[25%] lg:left-[27%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [-5, 5, -5], x: [3, -3, 3], rotate: [-2, 2, -2], duration: 5.7, delay: 2.2 }
  },
  {
    name: "Accenture",
    logo: companyLogos.Accenture,
    size: "large",
    pos: "bottom-[6%] left-[37%] lg:left-[39%]",
    zIndex: "z-15",
    visibility: "flex",
    float: { y: [5, -5, 5], x: [-3, 3, -3], rotate: [2, -2, 2], duration: 4.8, delay: 2.3 }
  },
  {
    name: "JPMorgan",
    logo: companyLogos.JPMorgan,
    size: "large",
    pos: "bottom-[6%] right-[35%] lg:right-[37%]",
    zIndex: "z-15",
    visibility: "flex",
    float: { y: [-5, 5, -5], x: [3, -3, 3], rotate: [-2, 2, -2], duration: 5.0, delay: 2.5 }
  },
  {
    name: "Goldman Sachs",
    logo: companyLogos.GoldmanSachs,
    size: "large",
    pos: "bottom-[8%] right-[23%] lg:right-[25%]",
    zIndex: "z-15",
    visibility: "hidden sm:flex",
    float: { y: [6, -6, 6], x: [-3, 3, -3], rotate: [2, -2, 2], duration: 6.6, delay: 1.0 }
  }
];

export default function TrustedBy() {
  return (
    <section className="relative py-20 lg:py-28 min-h-[580px] sm:min-h-[640px] md:min-h-[700px] lg:min-h-[760px] flex items-center justify-center overflow-hidden bg-transparent">
      
      {/* Background Orbital Guide Lines */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-[88%] h-[84%] max-w-6xl rounded-[50%] border border-slate-200/50 shadow-[0_0_80px_rgba(124,58,237,0.03)]" />
        <div className="absolute w-[68%] h-[64%] max-w-4xl rounded-[50%] border border-slate-100/70" />
      </div>

      {/* Center Section Content */}
      <div className="relative z-30 text-center max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 md:px-10 lg:px-12 pointer-events-auto">
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass border border-[#7C3AED]/15 text-xs font-bold tracking-[0.18em] uppercase text-[#7C3AED] shadow-xs mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899]" />
          Loved by innovators worldwide 💜
        </span>

        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 uppercase font-display leading-[1.05] text-center max-w-4xl mx-auto">
          <span className="block">TRUSTED BY TEAMS AT</span>
          <span className="block mt-1 sm:mt-2">MODERN COMPANIES</span>
        </h2>

        <div className="w-16 h-1 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] mx-auto my-4 rounded-full opacity-90" />

        <p className="text-base md:text-lg text-gray-600 max-w-md mx-auto font-normal leading-relaxed">
          Join thousands of startups, enterprises, and creators who build, scale, and grow with Studlyf.
        </p>
      </div>

      {/* Floating Orbital Circular Cards */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
        {floatingLogos.map((item) => (
          <motion.div
            key={item.name}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: item.float.y,
              x: item.float.x,
              rotate: item.float.rotate
            }}
            whileHover={{
              scale: 1.15,
              zIndex: 60,
              transition: { duration: 0.25, ease: "easeOut" }
            }}
            transition={{
              opacity: { duration: 0.8, delay: item.float.delay * 0.2 },
              scale: { duration: 0.8, delay: item.float.delay * 0.2 },
              y: { duration: item.float.duration, repeat: Infinity, repeatType: "mirror", ease: "easeInOut", delay: item.float.delay },
              x: { duration: item.float.duration * 1.1, repeat: Infinity, repeatType: "mirror", ease: "easeInOut", delay: item.float.delay },
              rotate: { duration: item.float.duration * 1.2, repeat: Infinity, repeatType: "mirror", ease: "easeInOut", delay: item.float.delay }
            }}
            className={`group absolute floating-logo-card border border-slate-200/80 flex items-center justify-center cursor-pointer pointer-events-auto select-none ${item.pos} ${item.zIndex} ${item.visibility} ${sizeClasses[item.size].circle}`}
            title={item.name}
            data-testid={`trusted-company-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className={`flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 aspect-square overflow-hidden ${sizeClasses[item.size].icon}`}>
              {item.logo}
            </div>
            <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 text-[10.5px] font-bold tracking-wide text-slate-700 bg-white/95 px-2.5 py-0.5 rounded-full border border-slate-200 shadow-md whitespace-nowrap pointer-events-none">
              {item.name}
            </span>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
